import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Banner, Button, Dialog, Skeleton, useToast } from '../ds/components';
import { usePermissions } from '../lib/session';
import { errorMessage } from '../lib/api';
import { useContactRecord, useInvalidateContact } from '../modules/contacts/api';
import { CallEditor } from '../modules/contacts/editors/CallEditor';
import { incomingCallerLabel, isLive } from '../modules/telephony/callState';
import { useCall } from '../modules/telephony/useCall';
import styles from './IncomingCall.module.css';

/**
 * Inbound calls in the browser (docs/dev/api-dialer.md „Inbound calls", D-217): for dialer users the shell registers the
 * softphone for inbound calls; a ringing call opens „Eingehender Anruf" with the contact's name (or the number of a
 * caller the server just created), „Annehmen" and „Ablehnen" — the ringtone is the Voice SDK's. Accepting opens the
 * contact record with the call editor (call bar connected, result after hang-up as for outbound calls); inside a running
 * dialer session the page stays and the call editor opens in a panel above it instead.
 */
export function IncomingCallLayer() {
  const permissions = usePermissions();
  const dialer = permissions.tool('dialer') === 'ok';
  const call = useCall();
  const navigate = useNavigate();
  const location = useLocation();
  const [panelContactId, setPanelContactId] = useState<string | null>(null);
  const { listen } = call;

  useEffect(() => {
    if (dialer) listen();
  }, [dialer, listen]);

  const incoming = call.state.incoming;
  const label = incoming === null ? null : incomingCallerLabel(incoming);

  function accept() {
    const contactId = call.acceptIncoming();
    if (contactId === null) return;
    if (location.pathname.startsWith('/dialer/session/')) setPanelContactId(contactId);
    else void navigate(`/contacts/${contactId}?editor=call`);
  }

  return (
    <>
      <Dialog
        open={incoming !== null}
        onClose={call.rejectIncoming}
        title="Eingehender Anruf"
        width={560}
        closeOnScrim={false}
        closeOnEscape={false}
        footer={
          <>
            <Button variant="secondary" onClick={call.rejectIncoming}>
              Ablehnen
            </Button>
            <Button onClick={accept}>Annehmen</Button>
          </>
        }
      >
        <div className={styles.caller} aria-live="assertive">
          <span className={styles.dot} aria-hidden="true" />
          <span className={styles.name}>{label?.name}</span>
          {label?.number ? <span className={styles.number}>{label.number}</span> : null}
        </div>
      </Dialog>
      {panelContactId !== null ? (
        <InboundCallPanel contactId={panelContactId} onClose={() => setPanelContactId(null)} />
      ) : null}
    </>
  );
}

/** The call editor of an inbound call accepted inside a dialer session, floating above the session page. */
function InboundCallPanel({ contactId, onClose }: { contactId: string; onClose: () => void }) {
  const record = useContactRecord(contactId);
  const call = useCall();
  const invalidate = useInvalidateContact();
  const { toast } = useToast();
  const live = isLive(call.state.phase) && call.state.contactId === contactId;

  function close() {
    if (live) return;
    call.reset();
    onClose();
  }

  return (
    <aside className={styles.panel} aria-label="Eingehender Anruf">
      {record.data ? (
        <CallEditor
          record={record.data}
          task={null}
          onClose={close}
          onDone={(result) => {
            toast({ message: result.toast });
            void invalidate(contactId);
            onClose();
          }}
        />
      ) : record.isError ? (
        <Banner tone="danger">{errorMessage(record.error)}</Banner>
      ) : (
        <Skeleton variant="block" height={220} />
      )}
    </aside>
  );
}
